'use client';

/**
 * Drop-in wrapper around Next.js Image that gracefully handles:
 *   - 404s / network failures (onError swap → fallback)
 *   - Empty / null / undefined src (renders fallback immediately)
 *
 * Same prop shape as Next/Image so it works in both `fill` mode and
 * explicit width/height mode. Use this anywhere in the storefront
 * where the image URL might be stale, deleted, or never set.
 *
 * Default fallback: subtle background + image icon.
 * Override via the `fallback` prop (e.g. show first letter of a
 * category name, a brand initial, etc.).
 */
import { c as _c } from "react/compiler-runtime";
import Image, { type ImageProps } from 'next/image';
import { Image as ImageIcon } from '@phosphor-icons/react/dist/ssr';
import { type ReactNode, type SyntheticEvent, useEffect, useRef, useState } from 'react';
type SafeImageProps = Omit<ImageProps, 'src' | 'placeholder' | 'blurDataURL'> & {
  src: string | null | undefined;
  /** Custom fallback shown when src is missing or load fails. */
  fallback?: ReactNode;
  /** Optional className applied to the default fallback wrapper. */
  fallbackClassName?: string;
  /**
   * Base64 LQIP data URL (typically a 10×10 JPEG). Stored alongside the
   * image row in `product_images.blur_data_url`, `categories.blur_data_url`,
   * etc. and fed into next/image's `placeholder="blur"` so the slot is
   * never transparent while the full-size image streams in.
   *
   * Pass `null` (or omit) when the row pre-dates the blur backfill — the
   * component substitutes a tiny neutral placeholder so we still avoid
   * the "pop" effect.
   */
  blurDataURL?: string | null;
  /**
   * Skip the blur placeholder. Use for the priority/LCP image: blur creates
   * a CSS background-image swap that confuses Lighthouse's LCP element
   * detection (the candidate keeps changing from the blur SVG to the real
   * image), so it never settles on a stable element. Trade-off: on the
   * priority image you typically already have it preloaded so the gap is
   * tiny — no real visual regression.
   */
  skipBlur?: boolean;
};

/**
 * 1×1 mid-gray PNG. Used as the fallback LQIP when the row has no
 * blur_data_url yet (legacy media, external URLs, backfill not run).
 * 70 bytes — cheaper than the data shape needed to call into next/image's
 * empty placeholder branch.
 */
const NEUTRAL_BLUR_DATA_URL = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkqAcAAIUAgUW0RjgAAAAASUVORK5CYII=';
export function SafeImage(t0) {
  const $ = _c(43);
  let alt;
  let blurDataURL;
  let fallback;
  let fallbackClassName;
  let onError;
  let onLoad;
  let rest;
  let skipBlur;
  let src;
  if ($[0] !== t0) {
    ({
      src,
      alt,
      fallback,
      fallbackClassName,
      blurDataURL,
      skipBlur,
      onLoad,
      onError,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = alt;
    $[2] = blurDataURL;
    $[3] = fallback;
    $[4] = fallbackClassName;
    $[5] = onError;
    $[6] = onLoad;
    $[7] = rest;
    $[8] = skipBlur;
    $[9] = src;
  } else {
    alt = $[1];
    blurDataURL = $[2];
    fallback = $[3];
    fallbackClassName = $[4];
    onError = $[5];
    onLoad = $[6];
    rest = $[7];
    skipBlur = $[8];
    src = $[9];
  }
  const [errored, setErrored] = useState(false);
  const imgRef = useRef(null);
  let t1;
  let t2;
  if ($[10] !== errored || $[11] !== src) {
    t1 = () => {
      if (!src || errored) {
        return;
      }
      const el = imgRef.current;
      if (el && el.complete && el.naturalWidth === 0) {
        setErrored(true);
      }
    };
    t2 = [src, errored];
    $[10] = errored;
    $[11] = src;
    $[12] = t1;
    $[13] = t2;
  } else {
    t1 = $[12];
    t2 = $[13];
  }
  useEffect(t1, t2);
  let t3;
  if ($[14] !== onError) {
    t3 = e => {
      setErrored(true);
      onError?.(e);
    };
    $[14] = onError;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const handleError = t3;
  let t4;
  if ($[16] !== onError || $[17] !== onLoad) {
    t4 = e_0 => {
      const target = e_0.currentTarget;
      if (target.naturalWidth === 0) {
        setErrored(true);
        onError?.(e_0);
        return;
      }
      onLoad?.(e_0);
    };
    $[16] = onError;
    $[17] = onLoad;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const handleLoad = t4;
  const hasSrc = typeof src === "string" && src.length > 0;
  if (!hasSrc || errored) {
    if (fallback !== undefined) {
      let t5;
      if ($[19] !== fallback) {
        t5 = <>{fallback}</>;
        $[19] = fallback;
        $[20] = t5;
      } else {
        t5 = $[20];
      }
      return t5;
    }
    const fillMode = "fill" in rest && rest.fill;
    if (fillMode) {
      const t5 = `absolute inset-0 flex items-center justify-center bg-subtle text-muted-foreground ${fallbackClassName ?? ""}`;
      const t6 = typeof alt === "string" ? alt : "";
      let t7;
      if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <ImageIcon className="size-1/3 max-w-12 max-h-12 opacity-50" />;
        $[21] = t7;
      } else {
        t7 = $[21];
      }
      let t8;
      if ($[22] !== t5 || $[23] !== t6) {
        t8 = <div className={t5} role="img" aria-label={t6}>{t7}</div>;
        $[22] = t5;
        $[23] = t6;
        $[24] = t8;
      } else {
        t8 = $[24];
      }
      return t8;
    }
    const width = (rest as {
      width?: number;
    }).width;
    const height = (rest as {
      height?: number;
    }).height;
    const t5 = `flex items-center justify-center bg-subtle text-muted-foreground ${rest.className ?? ""} ${fallbackClassName ?? ""}`;
    let t6;
    if ($[25] !== height || $[26] !== width) {
      t6 = {
        width,
        height
      };
      $[25] = height;
      $[26] = width;
      $[27] = t6;
    } else {
      t6 = $[27];
    }
    const t7 = typeof alt === "string" ? alt : "";
    let t8;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <ImageIcon className="size-1/3 max-w-12 max-h-12 opacity-50" />;
      $[28] = t8;
    } else {
      t8 = $[28];
    }
    let t9;
    if ($[29] !== t5 || $[30] !== t6 || $[31] !== t7) {
      t9 = <div className={t5} style={t6} role="img" aria-label={t7}>{t8}</div>;
      $[29] = t5;
      $[30] = t6;
      $[31] = t7;
      $[32] = t9;
    } else {
      t9 = $[32];
    }
    return t9;
  }
  let t5;
  if ($[33] !== blurDataURL || $[34] !== skipBlur) {
    t5 = skipBlur ? {} : {
      placeholder: "blur" as const,
      blurDataURL: blurDataURL || NEUTRAL_BLUR_DATA_URL
    };
    $[33] = blurDataURL;
    $[34] = skipBlur;
    $[35] = t5;
  } else {
    t5 = $[35];
  }
  let t6;
  if ($[36] !== alt || $[37] !== handleError || $[38] !== handleLoad || $[39] !== rest || $[40] !== src || $[41] !== t5) {
    t6 = <Image ref={imgRef} src={src} alt={alt} onError={handleError} onLoad={handleLoad} {...t5} data-safeimage="" {...rest} />;
    $[36] = alt;
    $[37] = handleError;
    $[38] = handleLoad;
    $[39] = rest;
    $[40] = src;
    $[41] = t5;
    $[42] = t6;
  } else {
    t6 = $[42];
  }
  return t6;
}